/**
 * MultiSelectDropdown Component
 * Multi-select checkbox group inside a styled popover.
 * Matches Figma modal designs: olive checkmarks, dark bg, consistent spacing.
 * Used for: Access/visibility settings (who can see, comment, edit, join).
 */

import { ChevronsUpDown } from "lucide-react"
import { useCallback, useMemo, useState } from "react"
import { Checkbox } from "@/components/ui/checkbox"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { useIsMobile } from "@/hooks/useIsMobile"
import { cn } from "@/lib"

export type MultiSelectOption = string | { value: string; label: string }

interface MultiSelectDropdownProps {
  label?: string
  options: MultiSelectOption[]
  selectedOptions: string[]
  onChange: (options: string[]) => void
  disabledOptions?: string[]
  autoSelectedOptions?: string[]
  groupExclusion?: {
    group1: string[]
    group2: string[]
  }
  selectAllWith?: Record<string, string[]>
  /** When all children are selected, auto-select the parent. */
  autoSelectParent?: { parent: string; children: string[] }
  /** When any of these options is selected, display only that option in the trigger (not the full list). */
  collapseDisplayOptions?: string[]
}

/** Normalize option to { value, label } */
function normalizeOption(opt: MultiSelectOption): { value: string; label: string } {
  return typeof opt === "string" ? { value: opt, label: opt } : opt
}

export function MultiSelectDropdown({
  label,
  options,
  selectedOptions,
  onChange,
  disabledOptions = [],
  autoSelectedOptions = [],
  groupExclusion,
  selectAllWith,
  autoSelectParent,
  collapseDisplayOptions,
}: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const isMobile = useIsMobile()

  const normalized = options.map(normalizeOption)

  const isInGroup1 = useCallback(
    (value: string): boolean => {
      return groupExclusion?.group1.includes(value) ?? false
    },
    [groupExclusion],
  )

  const isInGroup2 = useCallback(
    (value: string): boolean => {
      return groupExclusion?.group2.includes(value) ?? false
    },
    [groupExclusion],
  )

  const toggleOption = useCallback(
    (value: string) => {
      if (disabledOptions.includes(value)) return
      if (autoSelectedOptions.includes(value)) return

      const isCurrentlySelected = selectedOptions.includes(value)

      if (isCurrentlySelected) {
        let newSelection = selectedOptions.filter(v => v !== value)
        if (newSelection.length === 0) return

        // Auto-deselect parent when a child is deselected
        if (autoSelectParent) {
          const { parent, children } = autoSelectParent
          if (children.includes(value) && newSelection.includes(parent)) {
            const allChildrenSelected = children.every(c => newSelection.includes(c))
            if (!allChildrenSelected) {
              newSelection = newSelection.filter(v => v !== parent)
            }
          }
        }

        onChange(newSelection)
        return
      }

      let newSelection = [...selectedOptions, value]

      if (groupExclusion) {
        if (isInGroup1(value)) {
          newSelection = newSelection.filter(v => !isInGroup2(v))
        } else if (isInGroup2(value)) {
          newSelection = newSelection.filter(v => !isInGroup1(v))
        }
      }

      if (selectAllWith?.[value]) {
        const allValues = new Set([...newSelection, ...selectAllWith[value]])
        newSelection = Array.from(allValues)
      }

      // Auto-select parent when all children are selected
      // Auto-deselect parent when a child is deselected
      if (autoSelectParent) {
        const { parent, children } = autoSelectParent
        if (children.includes(value)) {
          const allChildrenSelected = children.every(c => newSelection.includes(c))
          if (allChildrenSelected && !newSelection.includes(parent)) {
            newSelection = [...newSelection, parent]
          } else if (!allChildrenSelected && newSelection.includes(parent)) {
            newSelection = newSelection.filter(v => v !== parent)
          }
        }
      }

      onChange(newSelection)
    },
    [
      selectedOptions,
      autoSelectedOptions,
      disabledOptions,
      groupExclusion,
      selectAllWith,
      autoSelectParent,
      isInGroup1,
      isInGroup2,
      onChange,
    ],
  )

  const displayText = useMemo(() => {
    const visibleSelections = selectedOptions

    if (visibleSelections.length === 0) {
      // Fallback: if autoSelectedOptions are available, show those
      const autoSelections = autoSelectedOptions
      if (autoSelections.length > 0) {
        if (collapseDisplayOptions) {
          const collapsed = collapseDisplayOptions.find(c => autoSelections.includes(c))
          if (collapsed) {
            return normalized.find(o => o.value === collapsed)?.label ?? collapsed
          }
        }
        return autoSelections.map(v => normalized.find(o => o.value === v)?.label ?? v).join(", ")
      }
      return "Select options"
    }
    // If a collapse option is selected, show only that
    if (collapseDisplayOptions) {
      const collapsed = collapseDisplayOptions.find(c => visibleSelections.includes(c))
      if (collapsed) {
        return normalized.find(o => o.value === collapsed)?.label ?? collapsed
      }
    }
    return visibleSelections.map(v => normalized.find(o => o.value === v)?.label ?? v).join(", ")
  }, [selectedOptions, autoSelectedOptions, normalized, collapseDisplayOptions])

  return (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1.25">
      {label && <span className="text-sm text-primary font-semibold flex-1">{label}</span>}

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="md:min-w-[200px] max-w-[70vw] w-fit md:max-w-none min-w-0 rounded-full md:rounded-none text-sm font-semibold text-primary text-left focus:outline-none bg-[#F2F0EB1A] md:bg-transparent transition-all duration-200 flex items-center md:justify-end gap-1.25 px-3.75 py-2.5 md:px-0 md:py-0"
          >
            <span className="truncate min-w-0">{displayText}</span>
            <ChevronsUpDown
              className={cn("transition-transform duration-200", isOpen && "rotate-180")}
              size={16}
              strokeWidth={2}
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align={isMobile ? "start" : "end"}
          sideOffset={10}
          className="min-w-[280px] bg-popover border border-border rounded-[20px] p-[20px]"
        >
          <div className="flex flex-col gap-[10px]">
            {normalized.map(option => {
              const isChecked = selectedOptions.includes(option.value)
              const isDisabled = disabledOptions.includes(option.value)
              const isForcedSelected = autoSelectedOptions.includes(option.value)
              return (
                <label
                  key={option.value}
                  className={cn(
                    "flex items-center gap-[10px]",
                    !isDisabled && "cursor-pointer",
                    isDisabled && "cursor-not-allowed",
                  )}
                >
                  <Checkbox
                    checked={isChecked || isForcedSelected}
                    onCheckedChange={() => toggleOption(option.value)}
                    disabled={isDisabled}
                    className="size-[24px] rounded-[6px]"
                  />
                  <span
                    className={cn(
                      "text-[14px]",
                      !isDisabled && (isChecked || isForcedSelected)
                        ? "font-semibold"
                        : "font-normal",
                      isDisabled && "opacity-60",
                    )}
                  >
                    {option.label}
                  </span>
                </label>
              )
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
